import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import DazzedHeading24 from '../../common/text/DazzedHeading24'
import DazzedParagraph12 from '../../common/text/DazzedParagraph12'
import {
  isOnFreePlan,
  isOnEnterprise,
  isOnGrinSubscription,
  getGrinSubscriptionPrices,
  isOnGrinSubscriptionV2
} from '../../../utils/billingUtils'
import { useTranslation } from 'react-i18next'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { useSelector } from 'react-redux'
import SubscriptionHeaderPlan from './SubscriptionHeaderPlan'
import SubscriptionPricingRows from './SubscriptionPricingRows'
import SubscriptionActions from './SubscriptionActions'

const useStyles = makeStyles(isNewPricing => ({
  root: {
    backgroundColor: 'var(--bg-color-5)',
    padding: 24,
    marginBottom: 20
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column'
  },
  contactToUpgrade: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10
  },
  price: {
    display: 'inline-block',
    fontFamily: 'Arial'
  },
  currency: {
    display: 'inline-block',
    fontFamily: 'Arial',
    fontWeight: 600,
    fontSize: 20
  },
  slash: {
    display: 'inline-block',
    fontFamily: 'Arial'
  },
  period: {
    display: 'inline-block',
    fontFamily: 'Arial',
    fontWeight: 600,
    fontSize: 20
  },
  planDescription: {
    display: 'flex',
    marginTop: 10,
    marginBottom: 32,
    justifyContent: 'space-between'
  },
  oldPricingWrapper: {
    flexDirection: 'row'
  }
}))

const SubscriptionOverview = ({
  grinPlan,
  price,
  activePatientsCount,
  maxPatients,
  onCancel,
  onContactUs,
  onUpgrade,
  renewalDate
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const practiceCountry = useSelector(state => state.practiceReducer.details?.country || 'US')

  const isFreePlan = useMemo(() => isOnFreePlan(grinPlan), [grinPlan])
  const isEnterprise = useMemo(() => isOnEnterprise(grinPlan), [grinPlan])
  const isSubscription = useMemo(() => isOnGrinSubscription(grinPlan) || isOnGrinSubscriptionV2(grinPlan), [grinPlan])

  const currentPricePerPatient = useMemo(
    () => getGrinSubscriptionPrices(practiceCountry)[grinPlan?.key],
    [grinPlan?.key, practiceCountry]
  )

  return (
    <div id="billing-subscription-overview" className={classes.root}>
      <div className={classes.headerContainer}>
        <SubscriptionHeaderPlan
          isTrial={isFreePlan}
          grinPlan={grinPlan}
          isSubscription={isSubscription}
          renewalDate={renewalDate}
        />
        {isSubscription && (
          <SubscriptionPricingRows currentPricePerPatient={currentPricePerPatient} price={price} grinPlan={grinPlan} />
        )}
        {!!price && !isFreePlan && !isEnterprise && !isSubscription ? (
          <div className={classes.oldPricingWrapper}>
            <DazzedHeading24 className={classes.price}>${price}</DazzedHeading24>
            <DazzedHeading24 className={classes.currency}>USD</DazzedHeading24>
            <DazzedHeading24 className={classes.slash}>/</DazzedHeading24>
            <DazzedHeading24 className={classes.period}>{t(`grinPlans.period.${grinPlan.period}Abbr`)}</DazzedHeading24>
          </div>
        ) : (
          <>
            {isFreePlan && (
              <DazzedParagraph14 className={classes.contactToUpgrade}>
                &nbsp;{t(`pages.accountSettings.billing.contactToUpgrade`)}
              </DazzedParagraph14>
            )}
          </>
        )}
      </div>
      {!isSubscription && !isFreePlan && (
        <div className={classes.planDescription}>
          <DazzedParagraph12>
            {activePatientsCount}/{maxPatients} {t('pages.accountSettings.billing.activeUsers')}
          </DazzedParagraph12>
        </div>
      )}
      <SubscriptionActions isTrial={isFreePlan} onCancel={onCancel} onUpgrade={onUpgrade} />
    </div>
  )
}

export default SubscriptionOverview
